import { useState } from 'react'
import { Stack } from '@mui/material'
import { com } from '@eidu/web'
import { PermissionWithId } from '../input/PermissionsInput'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import actionLabel from '../../domain/permission/actionLabel'
import GlobalSection from './GlobalSection'
import EntitiesSection from './EntitiesSection'
import UnassignedPermissionsList from './UnassignedPermissionsList'
import {
  ColDef,
  EntityRow,
  FORM_GLOBAL_ACTIONS,
  GlobalRow,
  anchorTypeIdOf,
  buildGlobalPermission,
  deriveUserEntityTypeId,
  isAllRowPermission,
  isAnchorable,
  isGlobalRowPermission,
  isUserSelectorAnchored,
} from './utils'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import GlobalPermission = com.eidu.sharedlib.entity.permission.GlobalPermission
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import Permission = com.eidu.sharedlib.entity.permission.Permission

type PermissionsMatrixProps = {
  entityTypes: readonly EntityType[]
  userSelector: EntitySelector
  selectedPermissions: readonly PermissionWithId[]
  setSelectedPermissions: (permissions: readonly PermissionWithId[]) => void
  selectedPolicies?: ReadonlyArray<PermissionPolicyDTO>
}

const PermissionsMatrix = ({
  entityTypes,
  userSelector,
  selectedPermissions,
  setSelectedPermissions,
  selectedPolicies,
}: PermissionsMatrixProps) => {
  const [expandedCell, setExpandedCell] = useState<string | null>(null)
  const [globalSectionOpen, setGlobalSectionOpen] = useState(true)
  const [entitiesSectionOpen, setEntitiesSectionOpen] = useState(true)

  const closeExpansion = () => setExpandedCell(null)

  const globalRows: GlobalRow[] = [
    ...GlobalAction.values().map<GlobalRow>((action) => ({
      id: `global-${action}`,
      action,
      label: actionLabel(action),
    })),
    ...FORM_GLOBAL_ACTIONS.map<GlobalRow>((action) => ({
      id: `global-${action}`,
      action,
      label: actionLabel(action),
    })),
  ]

  const entityRows: EntityRow[] = [...entityTypes]
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
    .map((type) => ({
      id: `type-${type.id.toString()}`,
      entityTypeId: type.id,
      entityType: type,
      label: String(type.name),
    }))

  const cellPermissions = (row: EntityRow, col: ColDef): PermissionWithId[] =>
    selectedPermissions.filter(({ permission }) => {
      if (permission.action !== col.action) return false
      const anchor = anchorTypeIdOf(permission, entityTypes)
      return anchor !== null && anchor.equals(row.entityTypeId)
    })

  const replacePermission = (id: string, newPermission: Permission) =>
    setSelectedPermissions(selectedPermissions.map((p) => (p.id === id ? { id, permission: newPermission } : p)))

  const removePermission = (id: string) => setSelectedPermissions(selectedPermissions.filter((p) => p.id !== id))

  const addPermission = (permission: Permission) =>
    setSelectedPermissions([...selectedPermissions, { id: crypto.randomUUID(), permission }])

  const toggleGlobalRow = (row: GlobalRow) => {
    closeExpansion()
    const existing = selectedPermissions.find((p) => isGlobalRowPermission(p.permission, row.action))
    if (existing) removePermission(existing.id)
    else addPermission(buildGlobalPermission(row.action))
  }

  const toggleEntityCell = (row: EntityRow, col: ColDef) => {
    closeExpansion()
    if (hasColumnAll(col)) narrowAllToOtherRows(row, col)
    else if (cellPermissions(row, col).length > 0) removeRowPermissions(row, col)
    else grantRow(row, col)
  }

  const grantRow = (row: EntityRow, col: ColDef) =>
    addPermission(new EntitySelectorPermission(col.action, new EntitySelector.OfType(row.entityTypeId)))

  const removeRowPermissions = (row: EntityRow, col: ColDef) => {
    const idsToRemove = new Set(cellPermissions(row, col).map((p) => p.id))
    setSelectedPermissions(selectedPermissions.filter((p) => !idsToRemove.has(p.id)))
  }

  const narrowAllToOtherRows = (row: EntityRow, col: ColDef) => {
    const withoutAll = selectedPermissions.filter(({ permission }) => !isAllRowPermission(permission, col.action))
    const fillIns: PermissionWithId[] = entityRows
      .filter((r) => !r.entityTypeId.equals(row.entityTypeId))
      .map((r) => ({
        id: crypto.randomUUID(),
        permission: new EntitySelectorPermission(col.action, new EntitySelector.OfType(r.entityTypeId)),
      }))
    setSelectedPermissions([...withoutAll, ...fillIns])
  }

  const hasColumnAll = (col: ColDef) =>
    selectedPermissions.some(({ permission }) => isAllRowPermission(permission, col.action))

  const toggleColumn = (col: ColDef) => {
    closeExpansion()
    if (hasColumnAll(col)) {
      setSelectedPermissions(selectedPermissions.filter(({ permission }) => permission.action !== col.action))
    } else {
      addPermission(new EntitySelectorPermission(col.action, EntitySelector.All))
    }
  }

  const toggleExpand = (key: string) => {
    setExpandedCell((prev) => (prev === key ? null : key))
  }

  const unassignedPermissions = selectedPermissions.filter(({ permission }) => {
    if (permission instanceof GlobalPermission) return false
    if (permission instanceof EntitySelectorPermission && EntitySelector.All.equals(permission.targets)) {
      const action = permission.action
      if (action instanceof EntityAction) return false
    }
    return !isAnchorable(permission)
  })

  const userEntityTypeId = deriveUserEntityTypeId(userSelector, entityTypes)
  const allowsPathForUser = isUserSelectorAnchored(userSelector)

  return (
    <Stack spacing={3} sx={{ width: '100%', overflowX: 'auto' }}>
      <GlobalSection
        rows={globalRows}
        open={globalSectionOpen}
        onToggleOpen={() => setGlobalSectionOpen((v) => !v)}
        isChecked={(row) => selectedPermissions.some((p) => isGlobalRowPermission(p.permission, row.action))}
        onToggleRow={toggleGlobalRow}
        selectedPolicies={selectedPolicies ?? []}
      />

      <EntitiesSection
        rows={entityRows}
        open={entitiesSectionOpen}
        onToggleOpen={() => setEntitiesSectionOpen((v) => !v)}
        cellPermissions={cellPermissions}
        onToggleCell={toggleEntityCell}
        onToggleColumn={toggleColumn}
        hasColumnAll={hasColumnAll}
        expandedCell={expandedCell}
        onToggleCellExpand={toggleExpand}
        onCloseExpansion={closeExpansion}
        entityTypes={entityTypes}
        allowsPathForUser={allowsPathForUser}
        userEntityTypeId={userEntityTypeId}
        addPermission={addPermission}
        replacePermission={replacePermission}
        removePermission={removePermission}
        selectedPolicies={selectedPolicies ?? []}
      />

      <UnassignedPermissionsList permissions={unassignedPermissions} onRemove={removePermission} />
    </Stack>
  )
}

export default PermissionsMatrix
